import { Button, Popover, Tab, Tabs, TabTitleIcon, TabTitleText } from "@patternfly/react-core";
import {
	type Dispatch,
	type FC,
	type MouseEvent,
	type RefObject,
	type SetStateAction,
	useEffect,
	useState,
} from "react";
import { useHistory, useRouteMatch } from "react-router-dom";
import "./ProductDetailSideNav.scss";
import { CheckCircleIcon, ExclamationCircleIcon, ExclamationTriangleIcon } from "@patternfly/react-icons";
import AIImportIcon from "../../../assets/ai-import-icon.svg?react";
import { validateFieldsForIconStatus } from "../../../constants/formValidationSchemas";
import { TabNames } from "../../../constants/product";
import type { ProductRouteParams } from "../../../constants/routerconstants";
import { useIsMounted } from "../../../hooks/useIsMounted";
import { useProductMetadata } from "../../../hooks/useProductMetadata";
import { usePublishState } from "../../../hooks/usePublishState";
import { useAIRateLimit } from "../../../queries/connectAI";
import Env from "../../../utils/env";

enum IconStatus {
	INCOMPLETE_REQUIRED_FIELDS = "INCOMPLETE_REQUIRED_FIELDS",
	INCOMPLETE_NON_REQUIRED_FIELDS = "INCOMPLETE_NON_REQUIRED_FIELDS",
	COMPLETE_FIELDS = "COMPLETE_FIELDS",
}

interface NavigationIconProps {
	iconStatus: IconStatus;
}

const NavigationIcon: FC<NavigationIconProps> = (props: NavigationIconProps) => {
	switch (props.iconStatus as IconStatus) {
		case IconStatus.INCOMPLETE_REQUIRED_FIELDS:
			return <ExclamationCircleIcon color="#C9190B" />;
		case IconStatus.INCOMPLETE_NON_REQUIRED_FIELDS:
			return <ExclamationTriangleIcon />;
		case IconStatus.COMPLETE_FIELDS:
			return <CheckCircleIcon />;
		default:
			return null;
	}
};

interface NavItem {
	text: string;
	path: string;
	icon?: IconStatus | undefined;
}

const ProductDetailSideNav: FC<{
	contentRefs: { current: { [key: number]: RefObject<any> } };
	activeTabKey: number;
	setActiveTabKey: Dispatch<SetStateAction<number>>;
	onImportWithAI: () => void;
}> = ({ contentRefs, activeTabKey, onImportWithAI }) => {
	const history = useHistory();
	const match = useRouteMatch<ProductRouteParams>();
	const { productDetail } = useProductMetadata();
	const [productDetailListingNavItems, setProductDetailListingNavItems] = useState<NavItem[]>([]);
	const publishState = usePublishState(match.params.productId);
	const isMounted = useIsMounted();
	const { data: rateLimitData, isSuccess: isRateLimitLoaded } = useAIRateLimit();
	const isImportDisabled = !isRateLimitLoaded || rateLimitData?.remaining === 0;

	useEffect(() => {
		const validationObject = publishState ? { ...productDetail, ...publishState } : productDetail;
		validateFieldsForIconStatus(validationObject).then((errorState) => {
			const tabIcons: Record<string, IconStatus | undefined> = {};
			(Object.keys(TabNames) as Array<keyof typeof TabNames>).forEach((key) => {
				const tabName = TabNames[key].name;
				if (
					![
						TabNames.features.name,
						TabNames.legal.name,
						TabNames.quickStart.name,
						TabNames.features.name,
						TabNames.faq.name,
						TabNames.versionMatrix.name,
					].includes(tabName)
				) {
					if (errorState[tabName]?.status) {
						tabIcons[key] = IconStatus.INCOMPLETE_REQUIRED_FIELDS;
					} else if (errorState[tabName]?.nonRequiredFieldCheck) {
						tabIcons[key] = IconStatus.INCOMPLETE_NON_REQUIRED_FIELDS;
					} else if (!errorState[tabName]?.status && !errorState[tabName]?.nonRequiredFieldCheck) {
						tabIcons[key] = IconStatus.COMPLETE_FIELDS;
					}
				}
			});
			if (isMounted()) {
				setProductDetailListingNavItems(
					(Object.keys(TabNames) as Array<keyof typeof TabNames>).map((key) => {
						return {
							text: TabNames[key].name,
							path: TabNames[key].route,
							icon: tabIcons[key],
						};
					}),
				);
			}
		});
	}, [productDetail, publishState, isMounted]);

	const handleTabClick = (_event: MouseEvent<HTMLElement>, tabIndex: string | number) => {
		const index = typeof tabIndex === "string" ? parseInt(tabIndex, 10) : tabIndex;
		const selectedTab = productDetailListingNavItems?.[index];
		if (selectedTab) {
			history.push(`${Env.routePrefix}/products/${match?.params?.productId}/${selectedTab.path}`);
			setTimeout(() => {
				document.getElementById(`tab-content-${index}`)?.focus();
			}, 0);
		}
	};

	const importButton = (
		<Button
			variant="secondary"
			icon={<AIImportIcon />}
			isAriaDisabled={isImportDisabled}
			onClick={isImportDisabled ? undefined : onImportWithAI}
		>
			Import with AI
		</Button>
	);

	return (
		<div className="product-detail-sidenav-container">
			<div className="product-detail-sidenav__import-ai">
				{isImportDisabled ? (
					<Popover
						aria-label="Generation limit reached"
						headerContent="Generation limit reached"
						bodyContent={`You can import fields ${rateLimitData?.limit} times within a 24-hour period. Please try again after your limit resets.`}
						triggerAction="hover"
					>
						{importButton}
					</Popover>
				) : (
					importButton
				)}
			</div>
			<Tabs
				activeKey={activeTabKey}
				onSelect={handleTabClick}
				isVertical
				className="product-detail-sidenav"
				aria-label="Product Information"
			>
				{productDetailListingNavItems?.map(({ text, icon }, index) => (
					<Tab
						key={index}
						eventKey={index}
						className="product-detail-sidenav__wrapper"
						tabContentId={`tab-content-${index}`}
						tabContentRef={contentRefs.current[index]}
						title={
							<>
								<TabTitleIcon className="product-detail-sidenav__wrapper__child">
									{icon && <NavigationIcon iconStatus={icon} />}
								</TabTitleIcon>{" "}
								<TabTitleText>{text}</TabTitleText>
							</>
						}
					/>
				))}
			</Tabs>
		</div>
	);
};
export default ProductDetailSideNav;
